import * as React from "react";

import { cn } from "@src/lib/utils/cn";
import { selectTriggerClassName } from "@src/lib/ui/Select";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@src/lib/ui/Popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@src/lib/ui/HoverCard";
import {
  MultiSelectMenu,
  type MultiSelectMenuOption,
} from "@src/lib/ui/MultiSelectMenu";
import XIcon from "@src/lib/icons/12px/x";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";

// A stable default, so the defaultValue sync effect doesn't loop.
const NO_VALUES: string[] = [];

// The gap between chips (gap-1), used by the fit measurement.
const CHIP_GAP_PX = 4;

/**
 * The field box: Select's trigger look as is (36px, rounded-lg, hairline
 * border, brand focus ring, aria-invalid error ring), on a div because the
 * chips' remove buttons can't nest in a button. The combobox button covers
 * the box, so its focus ring is drawn on the box. Left inset 7px, so chips
 * sit 7px in all round.
 */
const FIELD_CLASSNAME = cn(
  selectTriggerClassName,
  "group/field relative min-w-0 max-w-full justify-start gap-1 py-0 pl-[7px] pr-3",
  "has-[[role=combobox]:focus-visible]:border-brand has-[[role=combobox]:focus-visible]:ring-[3px] has-[[role=combobox]:focus-visible]:ring-brand-border",
  "aria-invalid:has-[[role=combobox]:focus-visible]:border-status-error-solid aria-invalid:has-[[role=combobox]:focus-visible]:ring-status-error-border",
  "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
);

// Small focus ring for the in-field buttons (chip ×, +N, clear all).
const INNER_FOCUS_CLASSNAME =
  "outline-none focus-visible:ring-2 focus-visible:ring-brand-border";

type MultiSelectOption = MultiSelectMenuOption;

/**
 * A 20px neutral value chip. Radius 4: nested in the 10px field at a 7px
 * inset (10 − 1 − 7 = 2, floored to 4). Kept local until a shared TagChip
 * lands.
 */
function MultiSelectChip({
  option,
  onRemove,
  shrink = false,
  measure,
  className,
}: {
  option: MultiSelectOption;
  onRemove?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  shrink?: boolean;
  // An inert copy at natural width for the fit measurement; `removable`
  // reserves the × slot.
  measure?: { removable: boolean };
  className?: string;
}): React.ReactElement {
  const Icon = option.icon;
  const hasLead = Boolean(option.avatar || Icon);
  const hasRemove = measure ? measure.removable : Boolean(onRemove);
  return (
    <span
      data-chip={measure ? undefined : option.value}
      data-measure={measure ? "chip" : undefined}
      className={cn(
        "inline-flex h-5 max-w-40 items-center gap-1 rounded bg-bg-tertiary text-xs-v4 font-normal-v4 text-text-primary",
        hasLead ? "pl-0.5" : "pl-1.5",
        hasRemove ? "pr-0.5" : "pr-1.5",
        shrink ? "min-w-0 shrink" : "shrink-0",
        className,
      )}
    >
      {option.avatar ? (
        <span className="flex shrink-0">{option.avatar}</span>
      ) : (
        Icon && <Icon className="size-4 shrink-0 text-icon-secondary" />
      )}
      <span className="min-w-0 truncate" title={option.label}>
        {option.label}
      </span>
      {measure && measure.removable && (
        <span className="-ml-0.5 size-4 shrink-0" />
      )}
      {!measure && onRemove && (
        <button
          type="button"
          data-chip-remove={option.value}
          aria-label={`Remove ${option.label}`}
          className={cn(
            "-ml-0.5 flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm text-icon-secondary transition-colors hover:bg-state-pressed hover:text-icon-primary",
            INNER_FOCUS_CLASSNAME,
          )}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(event);
          }}
          onKeyDown={(event) => {
            if (event.key === "Backspace" || event.key === "Delete") {
              event.preventDefault();
              event.stopPropagation();
              onRemove(event);
            }
          }}
        >
          <XIcon className="size-2.5" />
        </button>
      )}
    </span>
  );
}

/**
 * How many chips fit in `available` px, leaving room for "+N" when some
 * don't. Never fewer than one: the first chip truncates instead.
 */
export function fitChipCount(
  chipWidths: number[],
  available: number,
  moreWidth: number,
): number {
  const n = chipWidths.length;
  if (n === 0 || available <= 0) return n;
  const sum = (k: number) =>
    chipWidths.slice(0, k).reduce((total, w) => total + w, 0) +
    CHIP_GAP_PX * Math.max(0, k - 1);
  if (sum(n) <= available) return n;
  let k = n - 1;
  while (k > 1 && sum(k) + CHIP_GAP_PX + moreWidth > available) k -= 1;
  return Math.max(1, k);
}

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  options: MultiSelectOption[];
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  placeholder?: string;
  /**
   * @deprecated Ignored. Chips now fit the field's width and the rest
   * collapse into "+N".
   */
  maxCount?: number;
  /** @deprecated Ignored. Chips are always the neutral field chip. */
  variant?: "default" | "secondary" | "destructive" | "inverted" | null;
  /**
   * Set inside a Dialog: lets the list scroll under the Dialog's scroll
   * lock. The popover itself stays non-modal so the field's chips and +N
   * work while it is open.
   */
  dialogPopover?: boolean;
  asChild?: boolean;
  className?: string;
  contentClassName?: string;
  enableAll?: boolean;
  matchContentWidthToTrigger?: boolean;
  onSearchTermChange?: (term: string) => void;
  isLoading?: boolean;
  /** One summary text instead of chips (e.g. "3 locations"). */
  triggerLabel?: string;
  hideActions?: boolean;
  /** Error state: the status-error border and ring, like Input / Select. */
  error?: boolean;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      defaultValue = NO_VALUES,
      placeholder = "Select options",
      // Accepted for API compatibility but unused; destructured so they
      // don't reach the DOM button.
      /* eslint-disable @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars */
      maxCount,
      variant,
      asChild = false,
      /* eslint-enable @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars */
      dialogPopover = false,
      className,
      contentClassName,
      enableAll = true,
      matchContentWidthToTrigger = true,
      onSearchTermChange,
      isLoading = false,
      triggerLabel,
      hideActions = false,
      error = false,
      disabled = false,
      onKeyDown,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [visibleCount, setVisibleCount] = React.useState<number>(
      defaultValue.length,
    );
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const fieldRef = React.useRef<HTMLDivElement>(null);
    const moreCardRef = React.useRef<HTMLDivElement>(null);
    const rowRef = React.useRef<HTMLDivElement>(null);
    const measureRef = React.useRef<HTMLDivElement>(null);
    // Index of the chip whose × had focus when it was removed.
    const refocusIndexRef = React.useRef<number | null>(null);

    // Sync internal state with external defaultValue changes
    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    const invalid =
      error || ariaInvalid === true || ariaInvalid === "true" || undefined;

    const optionsByValue = React.useMemo(
      () => new Map(options.map((option) => [option.value, option])),
      [options],
    );
    const selectedOptions = selectedValues.flatMap((value) => {
      const option = optionsByValue.get(value);
      return option ? [option] : [];
    });
    const showChips = selectedOptions.length > 0 && !triggerLabel;

    const commit = (next: string[]) => {
      setSelectedValues(next);
      onValueChange(next);
    };
    const toggleOption = (value: string) => {
      commit(
        selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value],
      );
    };
    const removeValue = (
      value: string,
      event?: React.SyntheticEvent<HTMLButtonElement>,
    ) => {
      if (event && document.activeElement === event.currentTarget) {
        refocusIndexRef.current = Math.max(
          0,
          selectedOptions.findIndex((option) => option.value === value) - 1,
        );
      }
      commit(selectedValues.filter((v) => v !== value));
    };
    const handleClear = () => commit([]);
    const handleSelectAll = () => commit(options.map((option) => option.value));
    const removeLast = () => {
      if (selectedValues.length) commit(selectedValues.slice(0, -1));
    };

    // Keep focus in the field after a chip removes itself: the previous
    // chip's ×, else the combobox.
    React.useEffect(() => {
      const index = refocusIndexRef.current;
      if (index === null) return;
      refocusIndexRef.current = null;
      const removeButtons =
        rowRef.current?.querySelectorAll<HTMLButtonElement>(
          "[data-chip-remove]",
        ) ?? [];
      const target =
        removeButtons[Math.min(index, removeButtons.length - 1)] ??
        buttonRef.current;
      target?.focus();
    }, [selectedValues]);

    // Fit as many chips as the row holds; the rest collapse into "+N".
    // Natural widths come from an invisible copy of every chip.
    const selectedKey = selectedOptions
      .map((option) => `${option.value}\u0000${option.label}`)
      .join("\n");
    const measure = React.useCallback(() => {
      const row = rowRef.current;
      const layer = measureRef.current;
      if (!row || !layer) return;
      const widths = Array.from(
        layer.querySelectorAll<HTMLElement>("[data-measure=chip]"),
      ).map((el) => el.offsetWidth);
      const moreWidth =
        layer.querySelector<HTMLElement>("[data-measure=more]")?.offsetWidth ??
        0;
      setVisibleCount(fitChipCount(widths, row.clientWidth, moreWidth));
    }, []);

    React.useLayoutEffect(() => {
      if (!showChips) return;
      measure();
    }, [showChips, selectedKey, disabled, measure]);

    React.useEffect(() => {
      const row = rowRef.current;
      if (!showChips || !row) return;
      let observer: ResizeObserver | undefined;
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(() => measure());
        observer.observe(row);
      }
      // Chip widths change once the web font swaps in.
      void document.fonts?.ready.then(() => measure());
      return () => observer?.disconnect();
    }, [showChips, measure]);

    const shownCount = Math.min(
      Math.max(1, visibleCount),
      selectedOptions.length,
    );
    const shownOptions = selectedOptions.slice(0, shownCount);
    const hiddenOptions = selectedOptions.slice(shownCount);

    const accessibleValue = triggerLabel
      ? triggerLabel
      : selectedOptions.length
        ? selectedOptions.map((option) => option.label).join(", ")
        : placeholder;

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverAnchor asChild>
          <div
            ref={fieldRef}
            data-testid="multi-select"
            data-state={isPopoverOpen ? "open" : "closed"}
            aria-invalid={invalid}
            aria-disabled={disabled || undefined}
            className={cn(FIELD_CLASSNAME, className)}
          >
            <PopoverTrigger asChild>
              {/* A field trigger, not an action: it covers the box, and the
                  chips' buttons sit above it as siblings. */}
              <button
                ref={(node) => {
                  buttonRef.current = node;
                  if (typeof ref === "function") ref(node);
                  else if (ref) ref.current = node;
                }}
                type="button"
                // PopoverTrigger supplies aria-controls (its content id).
                // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                role="combobox"
                aria-expanded={isPopoverOpen}
                aria-invalid={invalid}
                disabled={disabled}
                // A combobox isn't named by its content: fall back to the
                // placeholder unless the caller labels it (aria-label,
                // aria-labelledby, or a <label htmlFor> on its id).
                aria-label={
                  props["aria-labelledby"] || props.id ? undefined : placeholder
                }
                {...props}
                onKeyDown={(event) => {
                  onKeyDown?.(event);
                  if (event.defaultPrevented) return;
                  if (event.key === "ArrowDown" && !isPopoverOpen) {
                    event.preventDefault();
                    setIsPopoverOpen(true);
                  }
                }}
                className="absolute -inset-px cursor-pointer rounded-[inherit] outline-none disabled:cursor-not-allowed"
              >
                <span className="sr-only">{accessibleValue}</span>
              </button>
            </PopoverTrigger>

            {showChips ? (
              // inline-size containment: the chips never widen the field (a
              // shrink-to-fit parent would otherwise grow to fit them all).
              // The row sits above the combobox; a click on it (or a chip's
              // label) still toggles the menu. The chips' own buttons stop
              // propagation.
              <div
                ref={rowRef}
                data-chip-row=""
                onClick={(event) => {
                  // The +N HoverCard is portaled, but its React events still
                  // bubble here.
                  if (event.currentTarget.contains(event.target as Node)) {
                    buttonRef.current?.click();
                  }
                }}
                className="relative flex min-w-0 flex-1 cursor-pointer items-center gap-1 overflow-hidden [contain:inline-size] group-aria-disabled/field:cursor-not-allowed"
              >
                {shownOptions.map((option, index) => (
                  <MultiSelectChip
                    key={option.value}
                    option={option}
                    shrink={index === 0}
                    onRemove={
                      disabled
                        ? undefined
                        : (event) => removeValue(option.value, event)
                    }
                  />
                ))}
                {hiddenOptions.length > 0 && (
                  <HoverCard openDelay={150} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <button
                        type="button"
                        data-testid="multi-select-more"
                        aria-label={`${hiddenOptions.length} more: ${hiddenOptions
                          .map((option) => option.label)
                          .join(", ")}`}
                        // Not disabled with the field: hovering still lists
                        // the hidden values.
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!disabled) setIsPopoverOpen(true);
                        }}
                        className={cn(
                          "inline-flex h-5 shrink-0 cursor-pointer items-center rounded px-1.5 text-xs-v4 font-medium-v4 text-text-secondary ring-1 ring-inset ring-border-primary transition-colors hover:bg-state-hover group-aria-disabled/field:cursor-not-allowed",
                          INNER_FOCUS_CLASSNAME,
                        )}
                      >
                        +{hiddenOptions.length}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      align="end"
                      ref={moreCardRef}
                      data-testid="multi-select-more-card"
                      className="flex w-auto max-w-64 flex-wrap gap-1 p-2"
                    >
                      {hiddenOptions.map((option) => (
                        <MultiSelectChip
                          key={option.value}
                          option={option}
                          className="max-w-full"
                          onRemove={
                            disabled
                              ? undefined
                              : () => removeValue(option.value)
                          }
                        />
                      ))}
                    </HoverCardContent>
                  </HoverCard>
                )}
                {/* Invisible copies at natural width, for the fit. */}
                <div
                  ref={measureRef}
                  aria-hidden
                  className="pointer-events-none invisible absolute top-0 left-0 flex w-max gap-1"
                >
                  {selectedOptions.map((option) => (
                    <MultiSelectChip
                      key={option.value}
                      option={option}
                      measure={{ removable: !disabled }}
                    />
                  ))}
                  <span
                    data-measure="more"
                    className="inline-flex h-5 items-center px-1.5 text-xs-v4 font-medium-v4"
                  >
                    +{selectedOptions.length}
                  </span>
                </div>
              </div>
            ) : (
              <span
                className={cn(
                  "pointer-events-none min-w-0 flex-1 truncate pl-[5px]",
                  triggerLabel && selectedOptions.length
                    ? "text-text-primary"
                    : "text-text-tertiary",
                )}
              >
                {triggerLabel && selectedOptions.length
                  ? triggerLabel
                  : placeholder}
              </span>
            )}

            {showChips && !disabled && (
              <button
                type="button"
                tabIndex={-1}
                aria-label="Clear selection"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClear();
                  buttonRef.current?.focus();
                }}
                className={cn(
                  "invisible relative flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-icon-secondary transition-colors hover:bg-state-hover hover:text-icon-primary",
                  "group-hover/field:visible group-focus-within/field:visible group-data-[state=open]/field:visible",
                  INNER_FOCUS_CLASSNAME,
                )}
              >
                <XIcon />
              </button>
            )}
            <ChevronsOpenIcon className="pointer-events-none relative ml-auto shrink-0 text-icon-secondary" />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className={cn(
            "flex min-w-60 flex-col p-0",
            matchContentWidthToTrigger && "popover-content-w-full",
            contentClassName,
          )}
          align="start"
          motion="menu"
          // Chips, +N and clear all live in the field but outside the
          // trigger: using them must not dismiss the menu.
          // A Dialog's scroll lock swallows wheel / touch scrolling outside
          // its content; keep the portaled list scrollable.
          onWheel={
            dialogPopover ? (event) => event.stopPropagation() : undefined
          }
          onTouchMove={
            dialogPopover ? (event) => event.stopPropagation() : undefined
          }
          // Non-modal, so focus isn't trapped: tabbing off either end of the
          // menu closes it and returns focus to the field (inside a Dialog
          // it would otherwise land outside the Dialog).
          onKeyDown={(event) => {
            if (event.key !== "Tab") return;
            const tabbables = Array.from(
              event.currentTarget.querySelectorAll<HTMLElement>(
                "input:not([disabled]), button:not([disabled]):not([tabindex='-1'])",
              ),
            );
            const first = tabbables[0];
            const last = tabbables[tabbables.length - 1];
            const active = document.activeElement as HTMLElement | null;
            // Focus can sit on a non-tabbable element (the Command root when
            // a short list has no search row); treat that as the start.
            const offList = !active || !tabbables.includes(active);
            if (
              tabbables.length === 0 ||
              (event.shiftKey ? active === first || offList : active === last)
            ) {
              event.preventDefault();
              setIsPopoverOpen(false);
              buttonRef.current?.focus();
            }
          }}
          onInteractOutside={(event) => {
            if (
              event.target instanceof Node &&
              (fieldRef.current?.contains(event.target) ||
                moreCardRef.current?.contains(event.target))
            ) {
              event.preventDefault();
            }
          }}
        >
          <MultiSelectMenu
            options={options}
            selectedValues={selectedValues}
            onToggle={toggleOption}
            onClear={handleClear}
            onSelectAll={enableAll ? handleSelectAll : undefined}
            // Server-searched lists always need the search row.
            searchable={onSearchTermChange ? true : undefined}
            onSearchChange={onSearchTermChange}
            onRemoveLast={removeLast}
            isLoading={isLoading}
            hideFooter={hideActions}
          />
        </PopoverContent>
      </Popover>
    );
  },
);

MultiSelect.displayName = "MultiSelect";

export default MultiSelect;
